/**
 * Phase 4 (Feb 2026) — Campaign Detail page.
 *
 * Concept §7.3: split operational status from economic verdict.
 *   * Operational chip     — "Läuft" / "Pausiert" / "Wird angehalten" …
 *   * Economic chip        — "im Rahmen" / "beobachten" / "nicht rentabel"
 * Plain-german KPIs (Anfragen, davon geprüft/passend, Kosten pro
 * Anfrage) sit on top; the raw fach-Zahlen (CTR, CPM, ROAS, CPL) are
 * ONE click deeper in a collapsible details panel.
 *
 * Also on this page:
 *   * "Was hat VIONA verändert" — chronological plain-german audit
 *     stream filtered to this campaign
 *   * Three inline handbrakes (cancel run / pause autopilot / stop
 *     delivery) wired to /api/v1/policy-guard/*
 *   * Pending approvals for this campaign inline
 *   * Connection cards with the 5-step ladder for every ad platform
 *     the campaign targets
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { toast } from "sonner";
import {
  CircleDot, TrendingUp, TrendingDown, Activity, ChevronDown, ChevronUp,
  Ban, PauseCircle, StopCircle, Loader2, ArrowRight,
} from "lucide-react";
import { api } from "../../lib/api";
import ConnectionStatusCard from "../../components/app/ConnectionStatusCard";
import AggregationConsumedBadge from "../../components/app/AggregationConsumedBadge";

const OPERATIONAL_LABEL = {
  active: { text: "Läuft", tone: "default", dot: "text-emerald-500" },
  published: { text: "Läuft", tone: "default", dot: "text-emerald-500" },
  scheduled: { text: "Bereit", tone: "outline", dot: "text-blue-500" },
  paused: { text: "Pausiert", tone: "secondary", dot: "text-amber-500" },
  stopping: { text: "Wird angehalten", tone: "secondary", dot: "text-amber-500" },
  stopped: { text: "Angehalten", tone: "outline", dot: "text-muted-foreground" },
  draft: { text: "Entwurf", tone: "outline", dot: "text-muted-foreground" },
  failed: { text: "Fehler", tone: "destructive", dot: "text-red-500" },
};

function economicVerdict(perf) {
  const roas = perf?.roas;
  const cpl = perf?.cpl_eur;
  if (roas == null && cpl == null) {
    return { text: "Zu wenig Daten", tone: "outline", icon: Activity };
  }
  if (roas != null) {
    if (roas >= 2) return { text: "Im Rahmen", tone: "default", icon: TrendingUp };
    if (roas >= 1) return { text: "Beobachten", tone: "secondary", icon: Activity };
    return { text: "Nicht rentabel", tone: "destructive", icon: TrendingDown };
  }
  if (cpl != null && cpl <= 30) return { text: "Im Rahmen", tone: "default", icon: TrendingUp };
  return { text: "Beobachten", tone: "secondary", icon: Activity };
}

const CAMPAIGN_AUDIT_PHRASES = {
  "campaigns.publish": () => "Kampagne veröffentlicht",
  "campaigns.stop_delivery": () => "Werbestopp angefordert",
  "campaigns.paused": () => "Kampagne pausiert",
  "campaigns.pause_requested": () => "Pause angefordert",
  "campaigns.resumed": () => "Kampagne wieder gestartet",
  "campaigns.resume_requested": () => "Wiederaufnahme angefordert",
  "campaigns.budget_change": (e) => {
    const eur = e?.after?.new_daily_eur;
    return eur != null
      ? `Tagesbudget geändert auf ${eur} €`
      : "Tagesbudget aktualisiert";
  },
  "campaigns.budget_updated": (e) => {
    const eur = e?.after?.new_daily_eur ?? e?.after?.amount;
    return eur != null
      ? `Tagesbudget geändert auf ${eur} €`
      : "Tagesbudget aktualisiert";
  },
  "approvals.granted": () => "Freigabe erteilt",
  "approvals.rejected": () => "Freigabe abgelehnt",
  "policy_guard.reconciled.confirmed_stopped": () =>
    "Anbieter-Bestätigung: Werbung ist gestoppt",
  "policy_guard.reconciled.provider_confirmed": () =>
    "Anbieter-Bestätigung erhalten",
  "policy_guard.reconciled.unclear": () =>
    "Status beim Anbieter unklar — Reconciler folgt nach",
  "autopilot.action.executed": () => "Autopilot-Aktion ausgeführt",
  "autopilot.paused": () => "Automatik pausiert",
  "orchestrator.run_cancelled": () => "Auftrag abgebrochen",
};

// Test-setup noise — never surfaced in the operator chronicle.
const CHRONICLE_HIDDEN_PREFIXES = ["test_fixtures."];

function startCaseDe(action) {
  // "campaigns.something_else" → "Campaigns · Something Else"
  return String(action || "")
    .split(".")
    .map((seg) => seg
      .split("_")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "))
    .join(" · ");
}

function toSentence(e) {
  const fn = CAMPAIGN_AUDIT_PHRASES[e.action];
  if (fn) return fn(e);
  return `Systemereignis: ${startCaseDe(e.action)}`;
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "gerade eben";
  if (mins < 60) return `vor ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `vor ${hrs} h`;
  return d.toLocaleDateString();
}

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [audit, setAudit] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [autopilotMode, setAutopilotMode] = useState(null);
  const [busy, setBusy] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lastAction, setLastAction] = useState(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function loadAll() {
      const [c, a, ap, pl, at] = await Promise.all([
        api.get(`/campaigns/${id}`).then((r) => r.data).catch(() => null),
        api
          .get(`/audit/events`, { params: { resource_id: id, limit: 30 } })
          .then((r) => r.data?.items || [])
          .catch(() => []),
        api
          .get(`/approvals`, { params: { state: "pending", resource_id: id, limit: 10 } })
          .then((r) => r.data?.items || [])
          .catch(() => []),
        api.get(`/ad-platforms`).then((r) => r.data?.platforms || []).catch(() => []),
        api.get(`/autopilot/policy`).then((r) => r.data?.mode || "off").catch(() => "off"),
      ]);
      if (!cancelled) {
        setCampaign(c);
        setAudit(a);
        setApprovals(ap);
        setPlatforms(pl);
        setAutopilotMode(at);
      }
    }
    loadAll();
    return () => { cancelled = true; };
  }, [id]);

  async function pauseAutopilot() {
    setBusy("pause");
    try {
      const r = await api.post("/policy-guard/autopilot/pause",
                                { reason: "campaign_detail" });
      const revoked = r.data?.revoked_delegations_count || 0;
      const scopes = r.data?.revoked_scope_ids || [];
      setLastAction({ kind: "pause", state: r.data?.state,
                       revoked, scopes });
      setAutopilotMode("off");
      toast.success(`Automatik pausiert · ${revoked} Delegationen widerrufen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Pause fehlgeschlagen");
    } finally { setBusy(null); }
  }

  async function stopDelivery() {
    setBusy("stop");
    setLastAction({ kind: "stop", state: "requested", phase: "A" });
    let backend = null;
    try {
      const r = await api.post(
        `/policy-guard/campaigns/${id}/stop-delivery`,
        { reason: "campaign_detail" });
      backend = r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Stopp fehlgeschlagen");
      setLastAction({ kind: "stop", state: "unclear", phase: "B" });
      setBusy(null);
      return;
    }
    toast("Stopp angefordert. Neue Aktionen gesperrt. Auslieferung wird angehalten und geprüft.");
    setTimeout(() => {
      setLastAction({ kind: "stop", phase: "B",
                       state: backend.state,
                       per_platform: backend.per_platform });
      const st = backend.state;
      if (st === "provider_confirmed" || st === "confirmed_stopped") {
        toast.success("Werbung ist beim Anbieter geprüft angehalten.");
      } else if (st === "partial") toast.warning("Teilweise erledigt.");
      else if (st === "unclear") toast.warning("Beim Anbieter ungeklärt.");
      setBusy(null);
    }, 2500);
  }

  async function cancelActiveRun() {
    // Find a running orchestrator run correlated with this campaign.
    setBusy("cancel");
    try {
      const runs = await api.get("/orchestrator/runs", {
        params: { status: "running", limit: 5, resource_id: id },
      }).then((r) => r.data?.items || []).catch(() => []);
      if (runs.length === 0) {
        toast("Kein laufender Auftrag zu dieser Kampagne.");
        return;
      }
      const r = await api.post(
        `/policy-guard/orchestration/cancel/${runs[0].id}`,
        { reason: "campaign_detail" });
      setLastAction({ kind: "cancel", state: r.data?.state,
                       jobs: r.data?.cancelled_jobs });
      toast.success(`Auftrag abgebrochen · ${r.data?.cancelled_jobs || 0} Jobs`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Abbruch fehlgeschlagen");
    } finally { setBusy(null); }
  }

  if (!campaign) {
    return (
      <Card data-testid="campaign-detail-loading">
        <CardContent className="py-8 text-sm text-muted-foreground text-center">
          Lade Kampagne …
        </CardContent>
      </Card>
    );
  }

  const op = OPERATIONAL_LABEL[campaign.publish_status || campaign.status] ||
    { text: campaign.status || "?", tone: "outline", dot: "text-muted-foreground" };
  const econ = economicVerdict(campaign.performance || {});
  const Econ = econ.icon;
  const perf = campaign.performance || {};

  return (
    <div data-testid="campaign-detail" className="space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/app/campaigns" className="underline hover:text-foreground">
            Kampagnen
          </Link>
          <span>›</span>
          <span>{campaign.name}</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 sm:flex-wrap">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight sm:flex-1 break-words"
              data-testid="campaign-detail-name">
            {campaign.name}
          </h1>
          <div className="flex flex-wrap gap-2">
            <Badge variant={op.tone} data-testid="campaign-detail-op-status">
              <CircleDot className={`w-3 h-3 mr-1 ${op.dot}`} />
              Betrieb: {op.text}
            </Badge>
            <Badge variant={econ.tone} data-testid="campaign-detail-econ-status">
              <Econ className="w-3 h-3 mr-1" />
              Wirtschaft: {econ.text}
            </Badge>
          </div>
        </div>
      </header>

      {/* Plain-german KPIs */}
      <Card data-testid="campaign-kpis">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Klartext-Zahlen</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Anfragen" value={perf.leads_total ?? "—"}
               testid="kpi-leads-total" />
          <KPI label="davon geprüft/passend" value={perf.leads_qualified ?? "—"}
               testid="kpi-leads-qualified" />
          <KPI label="Kosten pro Anfrage"
               value={perf.cpl_eur != null ? `${perf.cpl_eur} €` : "—"}
               testid="kpi-cpl" />
          <KPI label="Tagesbudget"
               value={campaign.budget?.amount != null ? `${campaign.budget.amount} €` : "—"}
               testid="kpi-daily-budget" />
          <div className="col-span-2 md:col-span-4">
            <AggregationConsumedBadge variant="block" />
          </div>
          <div className="col-span-2 md:col-span-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDetailsOpen((v) => !v)}
              data-testid="campaign-kpis-toggle-details"
            >
              {detailsOpen ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              Fachzahlen ein-/ausblenden
            </Button>
            {detailsOpen && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs"
                   data-testid="campaign-kpis-fachzahlen">
                <KPI label="CTR" value={perf.ctr != null ? `${(perf.ctr * 100).toFixed(2)} %` : "—"} testid="kpi-ctr" small />
                <KPI label="CPM" value={perf.cpm_eur != null ? `${perf.cpm_eur} €` : "—"} testid="kpi-cpm" small />
                <KPI label="CPC" value={perf.cpc_eur != null ? `${perf.cpc_eur} €` : "—"} testid="kpi-cpc" small />
                <KPI label="ROAS" value={perf.roas != null ? perf.roas.toFixed(2) : "—"} testid="kpi-roas" small />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inline handbrakes */}
      <Card data-testid="campaign-detail-controls" className="border-destructive/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Handbremsen</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Button
            variant="outline"
            onClick={cancelActiveRun}
            disabled={busy === "cancel"}
            data-testid="campaign-detail-cancel-run"
          >
            {busy === "cancel"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Ban className="w-4 h-4 mr-2" />}
            Auftrag abbrechen
          </Button>
          <Button
            variant="outline"
            onClick={pauseAutopilot}
            disabled={autopilotMode === "off" || busy === "pause"}
            data-testid="campaign-detail-pause-autopilot"
          >
            {busy === "pause"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <PauseCircle className="w-4 h-4 mr-2" />}
            Automatik pausieren
          </Button>
          <Button
            variant="outline"
            onClick={stopDelivery}
            disabled={busy === "stop" ||
                       !["published", "scheduled", "paused"].includes(campaign.publish_status)}
            data-testid="campaign-detail-stop-delivery"
          >
            {busy === "stop"
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <StopCircle className="w-4 h-4 mr-2" />}
            Werbung stoppen
          </Button>
          {lastAction && (
            <div
              className="col-span-1 md:col-span-3"
              data-testid="campaign-detail-last-action"
              data-phase={lastAction.phase || "B"}
              data-state={lastAction.state}
            >
              <Alert>
                <AlertTitle className="text-sm">
                  Letzte Aktion — {lastAction.kind}
                  {lastAction.kind === "stop" && lastAction.phase === "A" && (
                    <span className="text-xs font-normal ml-2 text-muted-foreground">
                      · wird geprüft …
                    </span>
                  )}
                </AlertTitle>
                <AlertDescription className="text-xs">
                  {lastAction.kind === "stop" && lastAction.phase === "A"
                    ? "Stopp angefordert. Neue Aktionen gesperrt. Auslieferung wird angehalten und geprüft."
                    : `State: ${lastAction.state}`}
                  {lastAction.kind === "pause" && (
                    <div className="mt-1 space-y-0.5">
                      <div>
                        Widerrufene Delegationen:{" "}
                        <span
                          className="font-mono"
                          data-testid="pause-autopilot-revoke-count"
                        >
                          {lastAction.revoked ?? 0}
                        </span>
                      </div>
                      <div
                        className="font-mono text-[11px] text-muted-foreground break-all"
                        data-testid="pause-autopilot-revoke-scopes"
                      >
                        {(lastAction.scopes || []).length === 0
                          ? "— keine Scopes —"
                          : (lastAction.scopes || []).join(", ")}
                      </div>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending approvals inline */}
      {approvals.length > 0 && (
        <Card data-testid="campaign-detail-approvals">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Offene Freigaben</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {approvals.map((a, i) => {
              const bp = (a?.param_binding?.hash || "").slice(0, 8);
              const isLegacy = !a?.param_binding?.hash;
              return (
                <div
                  key={a.id}
                  data-testid={`campaign-detail-approval-${i}`}
                  data-legacy={isLegacy ? "true" : "false"}
                  className="border rounded-md px-3 py-2 text-sm space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.kind}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        {bp ? (
                          <span className="font-mono">{bp}…</span>
                        ) : (
                          <Badge variant="destructive" className="text-[10px]">Legacy</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isLegacy}
                        title={isLegacy
                          ? "Legacy-Freigabe kann keine neue Klasse-C-Ausführung autorisieren. Bitte neue Freigabe einholen."
                          : "Klasse-C-Ausführung mit gebundenen Parametern starten"}
                        data-testid={`campaign-detail-approval-${i}-execute`}
                      >
                        Ausführen
                      </Button>
                      <Button size="sm" variant="ghost" asChild>
                        <Link to="/app/approvals">
                          Ansehen <ArrowRight className="w-3 h-3 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                  {isLegacy && (
                    <div
                      data-testid={`approval-legacy-warning-${a.id}`}
                      className="text-xs rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-destructive"
                    >
                      Legacy — für neue Klasse-C-Ausführung nicht autoritativ. Neue Freigabe nötig.
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Connections with 5-step ladder */}
      <Card data-testid="campaign-detail-connections">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Verbindungen</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(campaign.platforms || []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              Keine Plattform für diese Kampagne konfiguriert.
            </p>
          )}
          {(campaign.platforms || []).map((p) => {
            const platform = platforms.find((x) => x.platform === p);
            const conns = platform?.connections || [];
            const cap = perf?.per_platform?.[p]?.capability_state;
            return (
              <ConnectionStatusCard
                key={p}
                platform={p}
                connections={conns}
                capability_state={cap}
                testid={`campaign-detail-connection-${p}`}
              />
            );
          })}
        </CardContent>
      </Card>

      {/* "Was hat VIONA verändert" */}
      <Card data-testid="campaign-detail-chronicle">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Was hat VIONA verändert</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Änderungen erfasst.</p>
          ) : (
            <ol className="space-y-2">
              {audit
                .filter((e) => !CHRONICLE_HIDDEN_PREFIXES.some(
                  (p) => String(e.action || "").startsWith(p)))
                .slice(0, 15).map((e, i) => (
                <li
                  key={e.id || i}
                  data-testid={`campaign-detail-chronicle-item-${i}`}
                  className="flex items-center justify-between text-sm border-b last:border-b-0 py-2"
                >
                  <span>{toSentence(e)}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatWhen(e.created_at)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ label, value, testid, small }) {
  return (
    <div
      className="rounded border bg-background/60 px-3 py-2"
      data-testid={testid}
    >
      <div className={"text-muted-foreground " + (small ? "text-[10px]" : "text-xs")}>
        {label}
      </div>
      <div className={"font-mono " + (small ? "text-sm" : "text-lg")}>
        {value}
      </div>
    </div>
  );
}
